/**
 * Credential redaction for failure text and logs, the same on every runtime.
 *
 * `redactCredentials` hides every credential value in a text and keeps what
 * names it, so a message still says which setting leaked: `Authorization:
 * Bearer ***`, `?key=***`, `https://***@host`. `redactAndBound` redacts the
 * whole text first and only then cuts it to a byte budget, at a whitespace
 * boundary, so a cut can never expose part of a credential. There is no
 * cut-only helper: a caller cannot cut before redacting.
 *
 * Redaction runs over text of any length (whole process outputs, megabytes),
 * so every pattern runs in time linear in its input: a run a pattern scans
 * before it can fail either has a length bound or starts only where the run
 * itself starts, so no position is rescanned from each of the positions
 * before it.
 */

/** What replaces every credential value. It matches none of the patterns, so redacting twice changes nothing. */
export const redactionMarker = '***';

/** Where to keep text when `redactAndBound` has to cut: the start (`head`) or the end (`tail`, where errors usually are). */
export type TRedactAndBoundKeep = 'head' | 'tail';

export interface IRedactAndBoundOptions {
  /** Longest result in UTF-8 bytes, the marker included. At least the marker's byte length. */
  maxBytes: number;
  keep: TRedactAndBoundKeep;
  /** Stands where text was cut: after a head, before a tail. Defaults to `…`. */
  marker?: string;
}

export interface IRedactAndBoundResult {
  text: string;
  /** Whether text was cut. */
  truncated: boolean;
}

/** ANSI escape sequences: CSI (colours, cursor moves), OSC (titles, links) and two-character escapes. */
const ansiEscapePattern = /\u001b(?:\[[0-?]*[ -/]*[@-~]|\][^\u0007\u001b]*(?:\u0007|\u001b\\)|[@-Z\\-_])/g;

/**
 * Control characters other than tab and line feed (C0, DEL, C1, a carriage
 * return without a line feed) and the Unicode line and paragraph separators.
 * They become spaces, so a value cannot overwrite or forge a log line.
 */
const controlCharacterPattern = /[\u0000-\u0008\u000b-\u001f\u007f-\u009f\u2028\u2029]/g;

/** A credential form and the replacement that hides its value. */
interface ICredentialPattern {
  pattern: RegExp;
  replacement: string;
}

/** One redaction step: a pattern, or a function for a form one pattern cannot decide. */
type TRedactionStep = ICredentialPattern | ((textArg: string) => string);

/**
 * Name endings that mark a credential setting: NPM_TOKEN, DEPLOY_SECRET,
 * MINIO_SECRET_KEY, secretKey, DB_PASSWORD, GPG_PASSPHRASE, OPENAI_API_KEY,
 * x-api-key, AWS_SECRET_ACCESS_KEY, GIT_CREDENTIALS.
 */
const credentialNameEndings = String.raw`token|secret(?:[_-]?key)?|passw(?:or)?d|passphrase|api[_-]?key|access[_-]?key|private[_-]?key|credentials?`;

/**
 * A name of `charsArg` characters that ends in one of `endingsArg`, or in
 * PASS or PWD. PASS counts only as the whole name or after a _ or -, so BYPASS
 * is no credential; PWD counts only after one, so the shell's PWD and OLDPWD,
 * which hold directories, are none either.
 */
const credentialName = (charsArg: string, endingsArg: string): string =>
  String.raw`(?:${charsArg}*(?:${endingsArg})|(?:${charsArg}*[_-])?pass|${charsArg}*[_-]pwd)`;

/**
 * Name endings of object keys, environment variables and options, which also
 * name a credential by AUTH or AUTHORIZATION, a one-time password by OTP and
 * a JSON Web Token by JWT: Docker's "auth", HTTPAUTH, BASIC_AUTH,
 * --registry-auth, npm's --otp, ID_JWT.
 */
const credentialKeyEndings = String.raw`${credentialNameEndings}|auth(?:orization)?|otp|jwt`;

/** An environment variable or option name: NPM_TOKEN, DB_PASS, HTTPAUTH, --token. */
const settingName = credentialName(String.raw`\w`, credentialKeyEndings);

/**
 * A key whose value follows its colon without quotes, as in YAML and log
 * lines: password: …, GPG_PASSPHRASE: …, x-access-token: …, db_pass: …. PASS
 * and PWD count only after a _ or -, because test runners print "pass: 12",
 * and AUTH does not count, because "auth: required" is a message, not a
 * setting.
 */
const colonSettingName = String.raw`[\w-]*(?:${credentialNameEndings}|[_-](?:pass|pwd))`;

/**
 * A setting value: a quoted string, or the text up to the next whitespace. A
 * quote the line never closes hides the value to the end of the line.
 */
const settingValue = String.raw`(?:"[^"\n]*"?|'[^'\n]*'?|[^\s"']\S*)`;

/**
 * A value that is not already the marker, after an optional scheme from
 * `schemesArg`, so a header after an object key an earlier pattern hid
 * (`cookie: ***, theme: 'dark'`, `PRIVATE-TOKEN: Bearer ***`) keeps its
 * text and redacting twice changes nothing. A real value that merely starts
 * with *** is still hidden.
 */
const notRedacted = (schemesArg?: string): string =>
  String.raw`(?!${schemesArg ? String.raw`(?:(?:${schemesArg})[ \t]+)?` : ''}\*\*\*(?![^\s,;)\]}]))`;

/**
 * Authorization schemes whose credential is one word (RFC 7235 token68). The
 * scheme stays and only that word is hidden, so the text after it stays.
 */
const wordAuthorizationSchemes = String.raw`bearer|basic|token|dpop|negotiate|ntlm`;

/**
 * Authorization schemes whose credential is a parameter list: Digest
 * username="…", response="…"; AWS4-HMAC-SHA256 Credential=…, Signature=….
 * The scheme stays and the rest of the line is hidden.
 */
const parameterAuthorizationSchemes = String.raw`digest|hoba|mutual|vapid|oauth|hawk|aws4-hmac-sha256|scram-sha-(?:1|256)`;

/** Characters of an OAuth bearer token (RFC 6750 b64token). */
const tokenCharacters = String.raw`[A-Za-z0-9._~+/-]`;

/** Query and form parameter names that are credentials only as the whole name. */
const credentialParameterNames: ReadonlySet<string> = new Set([
  'code',
  'assertion',
  'client_assertion',
  'otp',
  'jwt',
  'session',
  'sessionid',
  'session_id',
  'sid',
]);

/**
 * Query and form parameter names that end in a credential word: every
 * setting name above (token, secret, password, passphrase, api_key, auth,
 * otp, jwt, pass, …), and sig, signature, credential(s) and session-id
 * endings (X-Amz-Signature, X-Amz-Credential, jsessionid, PHPSESSID). KEY
 * counts as the whole name, after a separator (api_key, X-Goog-Key) or after
 * a word that makes it a credential (apikey, accesskey), so monkey and turkey
 * keep their values. Names are at most 128 characters long, so the anchored
 * match is bounded.
 */
const credentialParameterNamePattern = new RegExp(
  String.raw`^(?:${credentialName(String.raw`[\w.-]`, credentialKeyEndings)}|[\w.-]*(?:sig|signature|credentials?|sessid|session[_-]?id)|(?:[\w.-]*[_.-])?key|[\w.-]*(?:api|access|secret|private|signing|auth|client|master|shared|encryption|subscription|license|app)key)$`,
  'i',
);

/** A camelCase name ending in Key: apiKey, sasKey. Case-sensitive, so monkey keeps its value. */
const camelCaseKeyPattern = /[a-z0-9]Key$/;

const isCredentialParameterName = (nameArg: string): boolean =>
  credentialParameterNames.has(nameArg.toLowerCase()) ||
  credentialParameterNamePattern.test(nameArg) ||
  camelCaseKeyPattern.test(nameArg);

/** The start of a query or form parameter: a ?, & or ; separator and a name of at most 128 characters. */
const parameterStartPattern = /[?&;]([\w.-]{1,128})=/g;

/** A parameter value: up to whitespace, the next separator, a fragment, a quote or an angle bracket. */
const parameterValuePattern = /[^\s&;#"'<>]*/y;

/**
 * Hides the values of credential parameters after ?, & and ;. A parameter
 * that is no credential keeps its value, and scanning goes on inside it, so
 * a nested URL (`?redirect=https://h/?token=…`) has its own credentials
 * hidden. The fragment and the parameters after a hidden value stay.
 */
const redactParameters = (textArg: string): string => {
  let result = '';
  let copiedUpTo = 0;
  parameterStartPattern.lastIndex = 0;
  for (let match = parameterStartPattern.exec(textArg); match; match = parameterStartPattern.exec(textArg)) {
    if (!isCredentialParameterName(match[1])) {
      continue;
    }
    const valueStart = parameterStartPattern.lastIndex;
    parameterValuePattern.lastIndex = valueStart;
    const valueEnd = valueStart + (parameterValuePattern.exec(textArg)?.[0].length ?? 0);
    if (valueEnd === valueStart) {
      continue;
    }
    result += textArg.slice(copiedUpTo, valueStart) + redactionMarker;
    copiedUpTo = valueEnd;
    parameterStartPattern.lastIndex = valueEnd;
  }
  return result + textArg.slice(copiedUpTo);
};

/**
 * Credential forms in the order they are redacted. Each pattern keeps what
 * names the credential and replaces only the value. Values stop at a line
 * end, so a pattern never reaches into the next line.
 */
const redactionSteps: ReadonlyArray<TRedactionStep> = [
  // PEM private key blocks, whole, across lines. A block cut off by the end
  // of the text is hidden up to that end. The key type before and after
  // PRIVATE KEY ("OPENSSH", "ENCRYPTED", " BLOCK") is bounded.
  {
    pattern: /(-----BEGIN [A-Z0-9 ]{0,40}PRIVATE KEY[A-Z ]{0,20}-----)[\s\S]*?(-----END [A-Z0-9 ]{0,40}PRIVATE KEY[A-Z ]{0,20}-----|$)/g,
    replacement: `$1${redactionMarker}$2`,
  },
  // URL userinfo: https://user:token@host, ssh://git@host. A user alone is
  // hidden too, because forges take a token as the user
  // (https://<token>@github.com). The host stays. The scheme is at most 32
  // characters long. The userinfo holds no character a URL must escape
  // (RFC 3986), so a URL ends at a quote or a bracket and an @ after it
  // ("https://host","user":"a@b") is no userinfo.
  {
    pattern: /\b([A-Za-z][A-Za-z0-9+.-]{0,31}:\/\/)[^\s/?#"<>{}[\]\\|^\x60]*@/g,
    replacement: `$1${redactionMarker}@`,
  },
  // JSON string values under a credential key: "_authToken": "…",
  // "password": "…", "secretKey": "…", "pass": "…", "token": "…", Docker's
  // "auth": "…", "authorization": "…", "x-api-key": "…", and the cookie
  // headers "cookie" and "set-cookie" (the whole key, so "tough-cookie"
  // keeps its version). A value the line never closes is hidden to the line
  // end.
  {
    pattern: new RegExp(
      String.raw`("(?:${credentialName(String.raw`[^"\n]`, credentialKeyEndings)}|(?:set-)?cookie)"[ \t]*:[ \t]*")(?:[^"\\\n]|\\.)*("?)`,
      'gi',
    ),
    replacement: `$1${redactionMarker}$2`,
  },
  // Unquoted values under a credential key in YAML and log lines:
  // password: hunter2, token: abc, GPG_PASSPHRASE: …. The colon follows the
  // name directly, so "Unexpected token : in JSON" keeps its text, and null,
  // undefined, true and false stay, so a printout still says that a token
  // was missing. A quoted value is left to the next pattern, and this one
  // runs first, so it never takes a hidden quoted value and the comma after
  // it for a value of its own. A Bearer, Basic or token scheme before the
  // value stays, so X-Auth-Token: Bearer … hides the credential, not the
  // scheme.
  {
    pattern: new RegExp(
      String.raw`((?<![\w-])${colonSettingName}:[ \t]*(?:(?:bearer|basic|token)[ \t]+)?)(?!(?:null|undefined|true|false)(?:[\s,;)\]}]|$))[^\s"'\x60]\S*`,
      'gi',
    ),
    replacement: `$1${redactionMarker}`,
  },
  // Quoted values under a credential key in JavaScript object and YAML
  // printouts: password: '…', secretKey: "…", token: '…', apiKey: '…',
  // 'PRIVATE-TOKEN': '…', authorization: 'Bearer …', cookie: '…'. \x60 is the
  // backtick Node prints around a string that holds both quote marks. Only a
  // quoted value is hidden, so "Unexpected token : in JSON" keeps its text.
  {
    pattern: new RegExp(
      String.raw`((?<![\w-])(?:${credentialName(String.raw`[\w-]`, credentialKeyEndings)}|(?:set-)?cookie)'?[ \t]*:[ \t]*)(?:'(?:[^'\\\n]|\\.)*'?|"(?:[^"\\\n]|\\.)*"?|\x60(?:[^\x60\\\n]|\\.)*\x60?)`,
      'gi',
    ),
    replacement: `$1${redactionMarker}`,
  },
  // Cookie lists as Node prints response headers: 'set-cookie': [ 'sid=…' ].
  // The list is hidden to its closing bracket or the line end.
  {
    pattern: /((?<![\w-])['"]?(?:set-)?cookie['"]?[ \t]*:[ \t]*\[)[^\]\n]*/gi,
    replacement: `$1${redactionMarker}`,
  },
  // Authorization and Proxy-Authorization headers, any scheme. A one-word
  // scheme (Bearer, Basic, token, …) stays and the word after it is hidden,
  // so "Authorization: Bearer *** (status 401)" keeps its status. A known
  // parameter scheme stays and the rest of the line is hidden. Any other
  // value is hidden to the line end, its first word included, because that
  // word may be the credential itself or an unknown scheme before it.
  {
    pattern: new RegExp(
      String.raw`(\b(?:proxy-)?authorization[ \t]*:[ \t]*)${notRedacted(`${wordAuthorizationSchemes}|${parameterAuthorizationSchemes}`)}(?:((?:${wordAuthorizationSchemes})[ \t]+)\S+|((?:${parameterAuthorizationSchemes})[ \t]+)?\S[^\r\n]*)`,
      'gi',
    ),
    replacement: `$1$2$3${redactionMarker}`,
  },
  // Other credential headers: GitLab's PRIVATE-TOKEN, JOB-TOKEN and
  // Deploy-Token, Api-Key (Anthropic-Api-Key too), and X- headers whose name
  // ends in a credential word: X-Api-Key, X-Goog-Api-Key, X-Auth-Token,
  // X-Registry-Auth, X-Amz-Security-Token. The part of an X- name before that
  // word is at most 40 characters long.
  {
    pattern: new RegExp(
      String.raw`(\b(?:(?:private|job|deploy)-token|api-key|x-[a-z0-9-]{0,40}?(?:token|api-key|secret|auth))[ \t]*:[ \t]*)${notRedacted('bearer|basic|token')}((?:(?:bearer|basic|token)[ \t]+)?)\S+`,
      'gi',
    ),
    replacement: `$1$2${redactionMarker}`,
  },
  // Cookie and Set-Cookie headers. A cookie list holds spaces ("a=1; b=2"),
  // so the value is hidden to the line end. The header name stands on its
  // own, so a package such as tough-cookie: 4.1.3 keeps its version.
  {
    pattern: new RegExp(String.raw`((?<![\w-])(?:set-)?cookie[ \t]*:[ \t]*)${notRedacted()}[^\s\[][^\r\n]*`, 'gi'),
    replacement: `$1${redactionMarker}`,
  },
  // A bearer token anywhere: "request failed with Bearer eyJ…". Only a
  // token-shaped value is hidden (16 characters or more, or holding a digit
  // or one of ._~+/-), so "Bearer authentication failed" and
  // WWW-Authenticate: Bearer realm="api" keep their text. The lookahead
  // scans only the run after one "Bearer".
  {
    pattern: new RegExp(
      String.raw`(\bbearer[ \t]+)(?=${tokenCharacters}*[0-9._~+/-]|${tokenCharacters}{16})${tokenCharacters}+=*`,
      'gi',
    ),
    replacement: `$1${redactionMarker}`,
  },
  // npm credential settings in .npmrc lines and npm_config_ variables:
  // //registry/:_authToken=…, _auth = …, npm_config__password=….
  {
    pattern: new RegExp(String.raw`(_(?:authToken|auth|password)[ \t]*[=:][ \t]*)${settingValue}`, 'gi'),
    replacement: `$1${redactionMarker}`,
  },
  // Query and form parameters after ?, & or ;: ?key=, &access_token=,
  // ?code=, ;jsessionid=, X-Amz-Signature=, as redactParameters describes.
  redactParameters,
  // Environment variables and options whose name ends in a credential word:
  // NPM_TOKEN=, NODE_AUTH_TOKEN=, *_SECRET=, SECRET_KEY=, *_PASSWORD=,
  // *_PASSPHRASE=, DB_PASS=, MYSQL_PWD=, *_API_KEY=, HTTPAUTH=, --token=,
  // password=, api_key=. A value without quotes is hidden to the next
  // whitespace, & included. BYPASS=, PWD= and OLDPWD= keep their values, as
  // credentialName describes. Parameters, names that follow a ?, & or ;
  // (looked up at most 64 characters back), are left to redactParameters,
  // which keeps the #fragment and the parameters after their value.
  {
    pattern: new RegExp(
      String.raw`(\b(?=${settingName}[ \t]*=)(?<![?&;][\w.-]{0,63})${settingName}[ \t]*=[ \t]*)${settingValue}`,
      'gi',
    ),
    replacement: `$1${redactionMarker}`,
  },
  // Environment variables whose upper-case name ends in _KEY: SSH_KEY=,
  // DEPLOY_KEY=, SIGNING_KEY=, and SSH_KEY: in a YAML env block. KEY alone
  // names too much to count in other names, so only an upper-case name, as
  // environment variables are written, counts: sort_key=, cacheKey: and
  // primary_key: keep their values. PUBLIC_KEY holds no secret and keeps its
  // value too. Parameters are left to redactParameters, as above.
  {
    pattern: new RegExp(String.raw`(\b(?<![?&;][\w.-]{0,63})[A-Z0-9_]*(?<!PUBLIC)_KEY(?:[ \t]*=|:)[ \t]*)${settingValue}`, 'g'),
    replacement: `$1${redactionMarker}`,
  },
  // Credential options followed by their value: --token …, --password …,
  // --secret …, --auth-token …, --api-key …, --passphrase …, --otp …. The
  // name ends in a credential word, so --password-stdin, --token-file and
  // --secret-id keep the argument after them, and a --no- option takes no
  // value.
  {
    pattern: new RegExp(
      String.raw`((?<![\w-])--(?!no-)${credentialName(String.raw`[\w-]`, credentialKeyEndings)}[ \t]+)${settingValue}`,
      'gi',
    ),
    replacement: `$1${redactionMarker}`,
  },
  // The password after -p in a registry login: docker login -u ci -p …,
  // podman login -p=…. -p names a port or a path in most other commands, so
  // it counts only after docker, podman, buildah, nerdctl or skopeo login,
  // at most 256 characters further on the line.
  {
    pattern: new RegExp(
      String.raw`(\b(?:docker|podman|buildah|nerdctl|skopeo)[ \t]+login\b[^\n]{0,256}?[ \t]-p(?:[ \t]+|=))${settingValue}`,
      'gi',
    ),
    replacement: `$1${redactionMarker}`,
  },
  // The password in a user:password argument after -u or --user: curl -u
  // ci:…, curl --user "ci:…". The user stays. Only a value that holds a
  // colon counts, so git push -u origin and sort -u keep their text; a
  // uid:gid (docker run -u 1000:1000) and a URL (-u https://host) do too.
  // The user is at most 256 characters long.
  {
    pattern:
      /((?<![\w-])(?:-u|--user)(?:[ \t]+|=))(?![0-9]{1,32}:[0-9]{1,32}(?!\S))(?:("[^:"\n]{0,256}:)[^"\n]*("?)|('[^:'\n]{0,256}:)[^'\n]*('?)|([^\s:"']{0,256}:)(?!\/\/)\S+)/g,
    replacement: `$1$2$4$6${redactionMarker}$3$5`,
  },
  // .netrc entries: machine host login ci password …, on one line or across
  // lines, default login ci password …, and machine host password … login
  // ci. A bare password counts only with a login in the same entry, so
  // "machine learning password policy" and "the default password is admin"
  // keep their text.
  {
    pattern: new RegExp(
      String.raw`(\b(?:machine[ \t]+\S+|default)(?:\s+account[ \t]+\S+)?(?:\s+login[ \t]+\S+(?:\s+account[ \t]+\S+)?\s+password[ \t]+|\s+password[ \t]+(?=${settingValue}\s+login[ \t])))${settingValue}`,
      'g',
    ),
    replacement: `$1${redactionMarker}`,
  },
  // npm access tokens.
  { pattern: /\bnpm_[A-Za-z0-9]{36}\b/g, replacement: redactionMarker },
  // GitHub classic personal, OAuth, user, server and refresh tokens, and
  // fine-grained personal access tokens.
  { pattern: /\b(?:gh[oprsu]_[A-Za-z0-9]{36,}|github_pat_[A-Za-z0-9_]{22,})\b/g, replacement: redactionMarker },
  // GitLab personal access tokens.
  { pattern: /\bglpat-[A-Za-z0-9_-]{20,}/g, replacement: redactionMarker },
  // Slack bot, user, app, refresh, legacy and rotating tokens.
  { pattern: /\bxox[abeprs]-[A-Za-z0-9-]{10,}/g, replacement: redactionMarker },
  // AWS access key IDs, long-term and temporary.
  { pattern: /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g, replacement: redactionMarker },
  // Anthropic (sk-ant-…) and OpenAI (sk-proj-…, sk-…) API keys.
  { pattern: /\bsk-[A-Za-z0-9_-]{24,}/g, replacement: redactionMarker },
  // Google API keys.
  { pattern: /\bAIza[0-9A-Za-z_-]{35,}/g, replacement: redactionMarker },
  // JSON Web Tokens: three base64url parts, the first a JSON object. A token
  // starts where a run of base64url characters starts.
  { pattern: /(?<![A-Za-z0-9_-])eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]*/g, replacement: redactionMarker },
];

/**
 * Hides every credential value in a text: private keys, URL userinfo,
 * authorization, API key and cookie headers, bearer tokens, credential
 * settings in JSON, YAML, npm configuration, query strings, environment
 * variables and options, and tokens whose shape identifies them (npm, GitHub,
 * GitLab, Slack, AWS, Anthropic, OpenAI, Google, JWT). ANSI escape
 * sequences are removed first, so colouring cannot split a credential from
 * its name, and every control character other than tab and line feed
 * becomes a space (a CRLF becomes a line feed), so the text cannot forge log
 * lines.
 */
export const redactCredentials = (textArg: string): string => {
  const plain = textArg.replace(ansiEscapePattern, '').replace(/\r\n/g, '\n').replace(controlCharacterPattern, ' ');
  return redactionSteps.reduce(
    (textSoFar, stepArg) => (typeof stepArg === 'function' ? stepArg(textSoFar) : textSoFar.replace(stepArg.pattern, stepArg.replacement)),
    plain,
  );
};

const textEncoder = new TextEncoder();

const utf8Length = (textArg: string): number => textEncoder.encode(textArg).byteLength;

/**
 * The UTF-8 width and UTF-16 length of the character starting at `indexArg`
 * (forward) or ending before it (backward). A surrogate pair is one
 * character of 4 bytes; a lone surrogate is written as U+FFFD (3 bytes), as
 * TextEncoder writes it.
 */
const characterAfter = (textArg: string, indexArg: number): { bytes: number; units: number } => {
  const unit = textArg.charCodeAt(indexArg);
  if (unit < 0x80) return { bytes: 1, units: 1 };
  if (unit < 0x800) return { bytes: 2, units: 1 };
  if (unit >= 0xd800 && unit <= 0xdbff) {
    const next = textArg.charCodeAt(indexArg + 1);
    if (next >= 0xdc00 && next <= 0xdfff) return { bytes: 4, units: 2 };
  }
  return { bytes: 3, units: 1 };
};

const characterBefore = (textArg: string, indexArg: number): { bytes: number; units: number } => {
  const unit = textArg.charCodeAt(indexArg - 1);
  if (unit < 0x80) return { bytes: 1, units: 1 };
  if (unit < 0x800) return { bytes: 2, units: 1 };
  if (unit >= 0xdc00 && unit <= 0xdfff && indexArg >= 2) {
    const previous = textArg.charCodeAt(indexArg - 2);
    if (previous >= 0xd800 && previous <= 0xdbff) return { bytes: 4, units: 2 };
  }
  return { bytes: 3, units: 1 };
};

const isWhitespace = (characterArg: string): boolean => /\s/.test(characterArg);

/**
 * Redacts the whole text with `redactCredentials`, then cuts it to at most
 * `maxBytes` UTF-8 bytes, the marker included. A cut never splits a
 * character or a surrogate pair, and it lands only on whitespace: the word
 * the budget would cut through is dropped whole, so no part of a value whose
 * context lies beyond the cut (the password before a URL's @) is kept. A
 * single word longer than the budget leaves only the marker.
 *
 * Throws a RangeError when `maxBytes` is not an integer or is smaller than
 * the marker.
 */
export const redactAndBound = (textArg: string, optionsArg: IRedactAndBoundOptions): IRedactAndBoundResult => {
  const marker = optionsArg.marker ?? '…';
  const markerBytes = utf8Length(marker);
  if (!Number.isInteger(optionsArg.maxBytes) || optionsArg.maxBytes < markerBytes) {
    throw new RangeError(`maxBytes must be an integer of at least ${markerBytes} (the marker's UTF-8 length), not ${optionsArg.maxBytes}`);
  }
  const text = redactCredentials(textArg);
  if (utf8Length(text) <= optionsArg.maxBytes) {
    return { text, truncated: false };
  }
  const budget = optionsArg.maxBytes - markerBytes;
  if (optionsArg.keep === 'head') {
    let end = 0;
    for (let used = 0; end < text.length; ) {
      const character = characterAfter(text, end);
      if (used + character.bytes > budget) break;
      used += character.bytes;
      end += character.units;
    }
    if (end > 0 && end < text.length && !isWhitespace(text[end - 1]) && !isWhitespace(text[end])) {
      while (end > 0 && !isWhitespace(text[end - 1])) end -= 1;
    }
    return { text: text.slice(0, end) + marker, truncated: true };
  }
  let start = text.length;
  for (let used = 0; start > 0; ) {
    const character = characterBefore(text, start);
    if (used + character.bytes > budget) break;
    used += character.bytes;
    start -= character.units;
  }
  if (start > 0 && start < text.length && !isWhitespace(text[start - 1]) && !isWhitespace(text[start])) {
    while (start < text.length && !isWhitespace(text[start])) start += 1;
  }
  return { text: marker + text.slice(start), truncated: true };
};
